'use client';

import { CSSProperties, useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { useWindowSize } from '../useWindowSize/useWindowSize';

type BreakpointType = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type Breakpoint = {
  key: BreakpointType;
  min: number;
  max: number;
};

type MediaQuery = Record<BreakpointType, CSSProperties['width']>;

export function useBreakpoint(mediaQuery?: MediaQuery): Breakpoint {
  const windowSize = useWindowSize();

  const theme = useContext(ThemeContext);

  if (!theme && !mediaQuery) {
    throw new Error('useBreakpoint must be used within ThemeProvider');
  }

  const queries: MediaQuery = mediaQuery || theme.mediaQuery;

  const breakpoints: BreakpointType[] = Object.keys(queries) as BreakpointType[];
  const values: number[] = Object.values(queries).map(x => parseInt(String(x)));

  const breakpoint = useMemo(() => {
    const breakpoint = values.reduce((p, c, i) => {
      if (c < windowSize.width) {
        p.key = breakpoints[i]!;
        p.min = c;
        if (i + 1 < values.length) {
          p.max = values[i + 1]!;
        } else {
          p.max = Number.POSITIVE_INFINITY;
        }
      }
      return p;
    }, { key: 'xs' as BreakpointType, min: -Number.NEGATIVE_INFINITY, max: -Number.NEGATIVE_INFINITY });
    return breakpoint;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize.width]);

  return breakpoint;
}
